import React, {useEffect, useState} from "react";
import {Button, ButtonGroup, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {Settings} from "@mui/icons-material";
import {ExportIcon} from "../../utils/icons";
import EnhancedTable from "../../components/table";
import {FormHelperEnhancedSelectWrapper} from "../../components/EnhancedSelect";
import CatalogManagerResults from "../../components/CatalogManagerResults";
import {utilGetPartners, utilGetPartnerScorecards, utilGetPartnerSuspensions, utilGetSupplierGroups} from "../../utils/utils";
import Moment from "moment";
import {Root, Header, defaultResultsContainerStyles, pageContentGap} from "../../theme";
import { styled } from '@mui/material/styles';
import {Field, Form} from "react-final-form";
import {calculator, IfEquals} from "../reports/scorecards";
import {FormHelperEnhancedTextWrapper} from "../../components/EnhancedTextField";
import FileSaver from "filesaver.js-npm";
import {PartnerPauseDialog} from "./components/PartnerPauseDialog";
import {PartnerUnpauseDialog} from "./components/PartnerUnpauseDialog";
import Config from "../../config";
import moment from "moment";
import {getCompanyNames, hoursToDays} from "../../helpers";
import CatalogCheckbox from "../../components/EnhancedCheckbox";
import {EventIdColumn, GroupsColumn} from "../catalogmanager/containers/catalog/components/catalog_columns";
import CatalogEventResults from "../catalogmanager/containers/catalog/components/catalog_event_results";
import { getEndDate } from "./partner_manager";
import GroupSupplierPopover from "./components/GroupSupplierPopover";
import UngroupSupplierPopover from "./components/UngroupSupplierPopover";


const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    '& .MuiButtonGroup-grouped': {
        borderColor: '#4F4D55',
        '&:hover': {
            borderColor: '#4F4D55',
            backgroundColor: theme.palette.grey[100],
        },
        '&.Mui-disabled': {
            borderColor: 'rgba(0, 0, 0, 0.26)',
            color: 'rgba(0, 0, 0, 0.26)',
        }
    }
}));

//region Helper Functions
const generateScorecardReport = async (scorecardData: any[], startDate: any, endDate: any) => {
    // Get extra suspensions data to append to report
    let suspensionData = await utilGetPartnerSuspensions();
    // utilGetPartnerSuspensions resolves to an array on success or {open, errorHeader, errorMessage}
    // on failure (errorMessage can be undefined). Discriminate on Array.isArray so a failure object
    // never reaches the `.find` below — keying off errorMessage would let an undefined-message failure
    // fall through as the error object and crash report generation.
    if (!Array.isArray(suspensionData)) suspensionData = [];

    let scorecards = "Partner,AccountNumber,VendorNumber,Groups,OverallScore,CancellationPercentageScore,ShipmentPercentageScore,ShipmentTimeScore,SalesTotal,OrderCount,PercentShipped,PercentCancelled,AverageTimeToShip,AverageDaysToShip,IsPaused,PauseReason,PauseStartDate,PauseEndDate,PauseMessage,Connection Type\r\n";
    let currentDateTime = new Date();
    for (let i = 0; i < scorecardData.length; i++) {
        let row = scorecardData[i];
        let suspension = suspensionData.find((s: any) => s.PartnerId === row.Id);
        if(suspension) {
            suspension.StartDate = moment(suspension.StartDate).local().format("YYYY-MM-DDTHH:mm:ss")
            suspension.EndDate = moment(suspension.EndDate).local().format("YYYY-MM-DDTHH:mm:ss")
        }

        scorecards += ["\"" + row.CompanyName + "\"",
            row.Id,
            row.VendorNumber ? "\"" + row.VendorNumber + "\"" : "",
            row.Groups && row.Groups.length > 0 ? "\"" + row.Groups.join(";") + "\"" : "",
            row.Combined?.Score ?? "N/A",
            row.CancelPercent?.Score ?? "N/A",
            row.ShipPercent?.Score ?? "N/A",
            row.ShipmentTime?.Score ?? "N/A",
            row.OrderTotalAmount ?? "",
            row.OrderCount ?? "N/A",
            row.ShipPercent?.ActualValue ?? "N/A",
            row.CancelPercent?.ActualValue ?? "N/A",
            row.ShipmentTime?.ActualValue ?? "N/A",
            hoursToDays(row.ShipmentTime?.ActualValue) ?? "N/A",
            suspension
                ?
                    currentDateTime >= new Date(suspension.StartDate)
                    && currentDateTime <= new Date(suspension.EndDate)
                : false,
            suspension?.ReasonCode ?? "",
            suspension?.StartDate ?? "",
            suspension?.EndDate ?? "",
            suspension?.Message ?? "",
            "\"" + (row.Type ?? "") + "\""
        ].join(",") + "\r\n";
    }

    let blob = new Blob([scorecards], {type: "application/octet-stream"});
    (FileSaver as any).saveAs(blob, `scorecards_${startDate}_${endDate}.csv`);
}
//endregion

//region Helper Components
const TableActions = (props: any) => {
    const {row} = props;
    const [pauseOpen, setPauseOpen] = React.useState(false);
    const [unpauseOpen, setUnpauseOpen] = React.useState(false);
    return (
        <Grid container style={{gap: "0.5em"}} justifyContent={"flex-end"}>
            <Button variant={"outlined"}
                    data-testid={`partner-action-${row.Id}`}
                    onClick={() => {
                        if (row.Status === "Live") setPauseOpen(true);
                        if (row.Status === "Paused") setUnpauseOpen(true);
                    }}
            >
                {row.Status === "Live" ? "Pause"
                    : row.Status === "Paused" ? "Unpause"
                        : "Unknown"}
            </Button>
            <Button variant={"outlined"}
                    component={Link}
                    to={`/partnermanagementcenter/partner?partnerId=${row.Id}`}
            >
                View
            </Button>
            {pauseOpen && (
                <PartnerPauseDialog open={pauseOpen} setOpen={setPauseOpen} partnerIds={[row.Id]}
                                    onPause={() => props.onAction(row.Id, 'Paused')}
                />
            )}
            {unpauseOpen && (
                <PartnerUnpauseDialog open={unpauseOpen} setOpen={setUnpauseOpen} partnerIds={[row.Id]}
                                      onUnpause={() => props.onAction(row.Id, 'Live')}
                />
            )}
        </Grid>
    );
}
//endregion

export const defaultEventConfig = {
    url: Config.api + "/odata/Company/VwActivityEvents",
    savedSearchEnabled: true,
    columns: [
        {
            id: 'EventDate',
            type: 'date',
            filterable: true,
            sortable: true,
            label: 'Date',
            template: (row) => (
                <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
            ),
            width: '15em'
        },
        {id: 'PartnerCoId', label: "Partner CoId", hidden: true, filterable: true, sortable: true, width: '8em'},
        {id: 'PartnerCompanyName', filterable: true, sortable: true, label: 'Partner', width: '14em', getOptions: getCompanyNames},
        {id: 'EventLevel', label: 'Severity', filterable: true, sortable: true, width: '6em'},
        {id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true},
        {id: 'Details', label: 'Details', filterable: true, allowStringOperators: true, width: '16em',
            hoverTemplate: (row, column) => row[column.id] || ''
        },
        {
            id: 'Viewed', label: "Viewed", sortable: true, template: (row) => (
                <CatalogCheckbox checked={row.Viewed} disabled={true} />
            )
        },
        {id: 'EventTypeId', hidden: true},
        {
            id: 'Id',
            width: '10em',
            stopPropagation: 'true',
            filterable: false,
            template: (row, column, colIndex, rowIndex, onAddFilter, setEventId, setEventViewed, setEventDialogOpen) =>
                <EventIdColumn
                    row={row} setEventId={setEventId} setEventViewed={setEventViewed}
                    setEventDialogOpen={setEventDialogOpen}/>
        },
        {id: 'CustomerViewable', hidden: true, filterable: false},
    ],
    order: 'desc',
    orderBy: 'Id',
    keyField: 'Id',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    title: "Event Log",
    refresh: false,
    filterInUrl: false,
    filter: {
        filters: [
            {field: "CustomerViewable", operator: 'eq', value: true, system: true},
            {field: "Category", operator: 'eq', value: "Suspension", system: true},
        ], logic: 'and'
    },
};

function PartnerStatusFilterButtons(props: any) {
    let selectedValue: any = "All";
    let previousFilter: any = null;

    if (props.table.state.filter != null) {
        let foundSync = props.table.state.filter.filters.find((f: any) => f.field === "Status");
        if (foundSync !== undefined) {
            selectedValue = foundSync.value;
            previousFilter = props.table.state.filter.filters.find((f: any) => f.field === "Status");
        }
    }

    const FilterButton = ({option, count, field, customFilter}: any) => {
        let filter = customFilter !== undefined
            ? customFilter
            : {
                field: field,
                operator: "eq",
                value: option,
            }

        return (
            <Button
                variant="outlined"
                color={selectedValue === option ? "primary" : "inherit"}
                onClick={async () => {
                    if (option === "All" && previousFilter != null)
                        props.table.extensions.onRemoveFilter(previousFilter);
                    else if (option === "All") {
                        // Do nothing
                    } else {
                        if (previousFilter != null) {
                            props.table.extensions.updateFilter(filter);
                        } else
                            props.table.extensions.onAddFilter(filter);
                    }
                }}
            >
                {option} ({count || 0})
            </Button>
        )
    }

    const statusButtons = ["All", "Live", "Paused"].map((option, index) => {
        return (
            <FilterButton key={index} option={option} field={"Status"} count={props.metaData[option]} />
        )
    });

    return (
        <>
            {statusButtons}
        </>
    );
}

export const BulkActions = (props: any) => {
    const {table, setConfig, setEventConfig, optimisticStatus} = props;
    const [bulkPauseOpen, setBulkPauseOpen] = React.useState(false);
    const [bulkUnpauseOpen, setBulkUnpauseOpen] = React.useState(false);
    const [bulkGroupOpen, setBulkGroupOpen] = React.useState(false);
    const [bulkGroupAnchor, setBulkGroupAnchor] = React.useState<any>(null);
    const [bulkUngroupOpen, setBulkUngroupOpen] = React.useState(false);
    const [bulkUngroupAnchor, setBulkUngroupAnchor] = React.useState<any>(null);
    const [bulkPartnerIds, setBulkPartnerIds] = React.useState<any[]>([]);

    const hasSelectedSuppliersWithGroups = React.useMemo(() => {
        if (!table.state.selected || table.state.selected.length === 0) return false;
        
        const selectedPartners = table.state.data?.filter((partner: any) =>
            table.state.selected.includes(partner.Id)
        ) || [];
        
        return selectedPartners.some(partner => 
            partner.Groups && partner.Groups.length > 0
        );
    }, [table.state.selected, table.state.data]);

    return (
        <Grid container style={{ gap: "10px" }}>
            <StyledButtonGroup
                variant="outlined"
                style={{
                    visibility: table.state.selected.length > 0 ? "visible" : "hidden",
                    opacity: table.state.selected.length > 0 ? 1 : 0,
                }}
            >
                <Button

                    onClick={() => {
                        setBulkPartnerIds(table.state.selected);
                        setBulkPauseOpen(true);
                    }}
                >
                    Pause
                </Button>

                <Button

                    onClick={() => {
                        setBulkPartnerIds(table.state.selected);
                        setBulkUnpauseOpen(true);
                    }}
                >
                    Unpause
                </Button>
            </StyledButtonGroup>

            <StyledButtonGroup
                variant="outlined"
                style={{
                    visibility: table.state.selected.length > 0 ? "visible" : "hidden",
                    opacity: table.state.selected.length > 0 ? 1 : 0,
                }}
            >
                <Button

                    onClick={(e) => {
                        setBulkPartnerIds(table.state.selected);
                        setBulkGroupAnchor(e.currentTarget);
                        setBulkGroupOpen(true);
                    }}
                >
                    Group
                </Button>

                <Button

                    onClick={(e) => {
                        setBulkPartnerIds(table.state.selected);
                        setBulkUngroupAnchor(e.currentTarget);
                        setBulkUngroupOpen(true);
                    }}
                    disabled={!hasSelectedSuppliersWithGroups}
                    style={{
                        opacity: hasSelectedSuppliersWithGroups ? 1 : 0.5,
                        cursor: hasSelectedSuppliersWithGroups ? 'pointer' : 'not-allowed'
                    }}
                >
                    Ungroup
                </Button>
            </StyledButtonGroup>

            <PartnerPauseDialog open={bulkPauseOpen} setOpen={setBulkPauseOpen} partnerIds={bulkPartnerIds}
                onPause={() => {
                    if (optimisticStatus) bulkPartnerIds.forEach(id => { optimisticStatus.current[id] = 'Paused'; });
                    setConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    setEventConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                }}
            />
            <PartnerUnpauseDialog open={bulkUnpauseOpen} setOpen={setBulkUnpauseOpen} partnerIds={bulkPartnerIds}
                onUnpause={() => {
                    if (optimisticStatus) bulkPartnerIds.forEach(id => { optimisticStatus.current[id] = 'Live'; });
                    setConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    setEventConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                }}
            />
            <GroupSupplierPopover 
                anchorEl={bulkGroupAnchor}
                open={bulkGroupOpen} 
                setOpen={setBulkGroupOpen} 
                partnerIds={bulkPartnerIds}
                onGroup={() => {
                    setConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    setEventConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                }}
            />
            <UngroupSupplierPopover 
                anchorEl={bulkUngroupAnchor}
                open={bulkUngroupOpen} 
                setOpen={setBulkUngroupOpen} 
                partnerIds={bulkPartnerIds}
                onGroup={() => {
                    setConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    setEventConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                }}
            />
        </Grid>
    )
}

const BufferExtension = (props: any) => {
    const {table, update, dataFilter} = props;

    const submit = async (values: any) => {
        if (values.grade !== dataFilter.grade) {
            let gradeRange = values.grade;

            if(gradeRange === undefined){
                if(table.state.filter != null) {
                    let foundSync = table.state.filter.filters.find((f) => f.field === "Score");
                    if (foundSync !== undefined) {
                        await table.extensions.removeFilters([foundSync]);
                    }
                }
            }
            else if(gradeRange === "NA") {
                let filter = {
                    field: "Score",
                    operator: "contains",
                    value: "N/A"
                }

                if(table.state.filter != null) {
                    let foundSync = table.state.filter.filters.find((f) => f.field === "Score");
                    if (foundSync !== undefined) {
                        await table.extensions.updateFilter(filter);
                    } else {
                        await table.extensions.onAddFilter(filter);
                    }
                } else {
                    await table.extensions.onAddFilter(filter);
                }
            }
            else {
                let selectedGrade = ["A", "B", "C", "D", "F"].filter(v => v.indexOf(values.grade) === 0)[0];
                let filter = {
                    field: "Score",
                    operator: "startswith",
                    value: selectedGrade
                }

                if(table.state.filter != null) {
                    let foundSync = table.state.filter.filters.find((f) => f.field === "Score");
                    if (foundSync !== undefined) {
                        await table.extensions.updateFilter(filter);
                    } else {
                        await table.extensions.onAddFilter(filter);
                    }
                } else {
                    await table.extensions.onAddFilter(filter);
                }
            }

            return;
        }

        update(values);
    }

    return (
        <Grid container
              style={{padding: "1em", gap: "0.5em", alignItems: "flex-end", width: "100%"}}>
            <Form onSubmit={submit} initialValues={dataFilter} decorators={[calculator]}
                  render={({handleSubmit, pristine, invalid}) => {
                      return (
                          <form onSubmit={handleSubmit} style={{display: 'flex', width: '100%', gap: "0.5em"}}>
                              <Field
                                  component={FormHelperEnhancedSelectWrapper as any}
                                  name='termType'
                                  label={"Date Range"}
                                  options={[
                                      {label: "Past 7 Days", value: "7days"},
                                      {label: "Past 30 Days", value: "30days"},
                                      {label: "This Month", value: "thisMonth"},
                                      {label: "Last Month", value: "lastMonth"},
                                      {label: "Custom", value: "custom"}
                                  ]}
                                  formControlProps={{style: {flex: 1}}}
                              />
                              <IfEquals field='termType' value='custom'>
                                  <Field component={FormHelperEnhancedTextWrapper} type='date' label='Start Date'
                                         name='start'/>
                                  <Field component={FormHelperEnhancedTextWrapper} type='date' label='End Date'
                                         name='end'/>
                              </IfEquals>
                              <Field
                                  style={{width: '10em', marginRight: '1rem'}}
                                  component={FormHelperEnhancedSelectWrapper as any}
                                  label='Letter Grade' name='grade' shrink='true' displayEmpty
                                  options={[
                                      {label: "Any", value: ""},
                                      {label: "A- to A+ ", value: "A"},
                                      {label: "B- to B+ ", value: "B"},
                                      {label: "C- to C+ ", value: "C"},
                                      {label: "D- to D+ ", value: "D"},
                                      {label: "F ", value: "F"},
                                      {label: "No Data ", value: "NA"}
                                  ]}
                                  formControlProps={{style: {flex: 1}}}
                              />
                              <div style={{alignSelf: 'flex-end'}}>
                                  <Button variant='contained' type='submit' disabled={pristine} color='primary'>
                                      Update
                                  </Button>
                              </div>
                          </form>
                      );
                  }}
            />
        </Grid>
    )
}

export default function PartnerManagementCenter(props: any) {
    const {setTitle} = props;
    const [reload, setReload] = React.useState(true);
    // partnerId -> intended Status after a pause/unpause; shown immediately and
    // cleared once the backend-computed Status catches up (see getData).
    const optimisticStatus = React.useRef({});
    const [dataFilter, setDataFilter] = React.useState({
        start: Moment().add(-7, 'days').format('YYYY-MM-DD'),
        end: Moment().format('YYYY-MM-DD'),
        termType: '7days',
        grade: ''
    });
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [eventsConfig, setEventsConfig] = useState<any>(defaultEventConfig);
    const [config, setConfig] = React.useState<any>(
        {
            getData: async () => {return []},
            savedSearchEnabled: true,
            columns: [
                {
                    id: "CompanyName",
                    label: 'Company Name',
                    width: '13em',
                    searchable: true,
                    sortable: true,
                    filterable: true,
                    getOptions: getCompanyNames
                },
                {
                    id: "Id",
                    hidden: true
                },
                {
                    id: "NoData",
                    hidden: true
                },
                {
                    id: "Score",
                    label: 'Score',
                    width: '8em',
                    toggleable: true,
                    sortable: true,
                    filterable: false,
                },
                {
                    id: "OrderCount",
                    label: 'Order Count',
                    width: '8em',
                    toggleable: true,
                    sortable: true
                },
                {
                    id: "Status",
                    label: 'Status',
                    width: '8em',
                    toggleable: true,
                    sortable: true,
                    template: (row, column, colIndex, rowIndex) => {
                        const colorStatus = row.Status === "Live"
                            ? "green"
                            : row.Status === "Paused"
                                ? "orange"
                                : "gray";
                        return (
                            <Chip
                                data-testid={`partner-status-${row.Id}`}
                                style={{color: "white", background: colorStatus}}
                                label={row.Status ?? "Unknown"}
                            />
                        );
                    }
                },
                {
                    id: "Type",
                    label: 'Type',
                    width: '6em',
                    toggleable: true,
                    sortable: true,
                    template: (row, column, colIndex, rowIndex) => {
                        return row.Type ? (
                            <Chip
                                style={{color: "white", background: "gray"}}
                                label={row.Type}
                            />
                        ) : '';
                    }
                },
                {
                    id: "Groups",
                    label: 'Supplier Group(s)',
                    width: '12em',
                    toggleable: true,
                    sortable: false,
                    filterable: false,
                    template: (row, column, colIndex, rowIndex) => <GroupsColumn row={row} />
                },
                {
                    filterable: false,
                    id: 'actions', width: '10em',
                    template: (row, column, colIndex, rowIndex) => (
                        <TableActions row={row}
                                      onAction={(rowId, newStatus) => {
                                          if (rowId != null && newStatus) optimisticStatus.current[rowId] = newStatus;
                                          setReload((prevState) => !prevState);
                                      }}
                        />
                    )
                }
            ],
            keyField: 'Id',
            order: 'asc',
            orderBy: 'CompanyName',
            pageSize: 20,
            pageSizes: [10, 20, 50, 100],
            refresh: false,
            title: `Partner Specific Settings`,
            filterInUrl: false,
            filter: null
        }
    );

    //region Helper Functions
    const update = (values) => {
        setDataFilter(values);
        setReload((prevState) => !prevState);
    }
    //endregion

    //region Lifecycle
    useEffect(() => {
        if(setTitle) {
            setTitle('Partner Management');
        }
    }, [setTitle]);

    useEffect(() => {
        if (config.getData) {
            const getData = async () => {
                setIsLoading(true);
                try {
                    // Get both partners & score data from selected timeframe and combine them //
                    let partners = await utilGetPartners();
                // These util lookups resolve to an array on success or to an error object on failure
                // (utilGetPartners/Scorecards -> {open, errorHeader, errorMessage}; utilGetSupplierGroups
                // -> {errorMessage}). errorMessage can be undefined, so discriminate on Array.isArray —
                // otherwise a failed lookup falls through as the error object and crashes the
                // .forEach/.map/.find below.
                if (!Array.isArray(partners)) partners = [];

                const startDate = Moment(dataFilter.start).toISOString();
                const endDate = getEndDate(dataFilter.end);

                let scoreCards = await utilGetPartnerScorecards(startDate, endDate, true);
                if (!Array.isArray(scoreCards)) scoreCards = [];

                let supplierGroups = await utilGetSupplierGroups();
                if (!Array.isArray(supplierGroups)) supplierGroups = [];

                const partnerGroupsMap = {};
                supplierGroups.forEach(group => {
                    if (group.Partners) {
                        group.Partners.forEach(partner => {
                            if (!partnerGroupsMap[partner.Id]) {
                                partnerGroupsMap[partner.Id] = [];
                            }
                            partnerGroupsMap[partner.Id].push(group.Name);
                        });
                    }
                });

                // Merge Data
                let data = partners.map(p => {
                    let score = scoreCards.find(s => s.Id === p.Id);
                    const groups = partnerGroupsMap[p.Id] || [];
                    
                    if (!score) return {Score: "N/A", OrderCount: "N/A", NoData: true, Groups: groups, ...p};
                    return {
                        ...p,
                        ...score,
                        Combined: score.Combined,
                        NoData: score.NoData ?? true,
                        Score: score.NoData ? "N/A" : score.Combined.LetterGrade,
                        OrderCount: score.OrderCount ?? "N/A",
                        Groups: groups,
                        VendorNumber: ((p.VendorCodes ?? []).find(r => r.System === "Portal") ?? {}).Code
                    };
                });

                // /api/v1/Partners.Status is server-computed from suspensions and can lag a
                // pause/unpause POST by more than the dialog buffer, so a single refetch may
                // still return the old status. Show the intended status immediately; drop the
                // override once the fetched Status matches (backend has caught up).
                data = data.map(row => {
                    const pending = optimisticStatus.current[row.Id];
                    if (pending == null) return row;
                    if (row.Status === pending) { delete optimisticStatus.current[row.Id]; return row; }
                    return {...row, Status: pending};
                });

                    setData(data);
                    return data;
                } finally {
                    setIsLoading(false);
                }
            };
            setConfig(prevState => ({
                ...prevState,
                getData: getData,
                refresh: reload
            }));
            setEventsConfig(prevState => ({
                ...prevState,
                refresh: reload
            }));
        }

        // eslint-disable-next-line
    }, [reload]);
    //endregion

    return (
        <Root style={{gap: pageContentGap, padding: "24px 14px 24px 14px"}}>
            <Grid container justifyContent={"space-between"} direction={"row"} alignItems={"center"} style={{width: "100%"}}>
                <Grid item>
                    <Header>
                        Partner Management Center
                    </Header>
                </Grid>

                <Grid item style={{display: "flex", gap: "8px"}}>
                    <Button variant='outlined'
                            startIcon={<Settings style={{height: "18px", width: "18px"}}/>}
                            component={Link}
                            to={'/partnermanagementcenter/settings'}
                    >
                        Settings
                    </Button>

                    <Tooltip title={isLoading ? "Data is loading, please wait..." : ""} arrow>
                        <span>
                            <Button variant='outlined'
                                    disabled={isLoading}
                                    startIcon={<ExportIcon style={{height: "18px", width: "18px"}}/>}
                                    onClick={() => generateScorecardReport(data, dataFilter.start, dataFilter.end)}
                            >
                                Generate Report
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid style={{width: "100%"}}>
                <EnhancedTable config={config} history={props.history} location={props.location}
                               // Remount on each reload (pause/unpause/refresh) so the table
                               // re-runs getData from scratch and re-renders the status chips.
                               // In-place config.refresh alone did not reliably reach the rendered
                               // rows; combined with the optimistic Status override in getData this
                               // shows the new status immediately despite the backend Status lag.
                               key={`partnermanagementcenter_table_${reload}`}
                               template={(table) =>
                                   <CatalogManagerResults
                                       showSavedSearch={true}
                                       savedSearchTableType="partner-management-center"
                                       bulkActions={<BulkActions table={table} setConfig={setConfig} setEventConfig={setEventsConfig} optimisticStatus={optimisticStatus}/>}
                                       filterShowExceptions={["Score"]}
                                       isSelectable={true}
                                       disableTermInUrl={true}
                                       condensed={false}
                                       history={props.history} location={props.location}
                                       table={table}
                                       config={config} setConfig={setConfig}
                                       resultsContainerStyles={defaultResultsContainerStyles}
                                       bufferExtensions={
                                              <BufferExtension table={table} update={update} dataFilter={dataFilter}/>
                                       }
                                       renderButtonFiltersAfter={false}
                                       buttonFilters={
                                           <Grid container style={{gap: "0.5em", flex: 1}}>
                                               <PartnerStatusFilterButtons
                                                   table={table}
                                                   metaData={{
                                                       "All": data.length,
                                                       "Live": data.filter(r => r.Status === "Live").length,
                                                       "Paused": data.filter(r => r.Status === "Paused").length
                                                   }}
                                               />
                                           </Grid>
                                       }
                                   />
                               }
                />
            </Grid>

            <Grid style={{width: "100%"}}>
                <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                               template={(table) =>
                                   <CatalogEventResults history={props.history} location={props.location}
                                                        table={table} config={eventsConfig}
                                                        condensed={true}
                                                        tableHeader={"Events"}
                                                        setConfig={setEventsConfig}
                                   />
                               }/>
            </Grid>
        </Root>
    );
}
